/**
 * Every URL that points into a P&ID: the path itself and the query parameters
 * that say what to open once the drawing loads. Builders and parsers sit
 * together so a parameter name is written once and read once.
 */

/**
 * The link that sends a reviewer to one equipment's edit panel on another
 * drawing. The wizard's Primary P&ID line names the drawing that owns an asset,
 * and following it opens that drawing with the same equipment already open.
 *
 * The parameter is consumed on arrival: the app opens the panel once and then
 * drops it from the URL, so a reload after closing the panel stays closed.
 */
export const EDIT_EQUIPMENT_PARAM = 'edit_equipment_id';

/**
 * The link that sends a reviewer to one instrument's editor. It is the
 * instrument twin of `EDIT_EQUIPMENT_PARAM`: it frames and selects the
 * instrument the way `target_instrument_id` does, and opens the instrument
 * editor on it as well.
 *
 * Consumed on arrival like the equipment one, so a reload after the reviewer
 * closes the editor stays closed.
 */
export const EDIT_INSTRUMENT_PARAM = 'edit_instrument_id';

/**
 * A URL parameter that links shared in Slack and Linear carry, and that Agent
 * review links append (see `buildAgentReviewHref`). The workspace reads it once,
 * as the drawing loads, and then drops it from the URL.
 */
export const STEP_PARAM = 'step';

/** The `?step=` value of an Agent review link: the drawing opens with the asset rail's
 *  Needs review filter on. */
export const AGENT_REVIEW_STEP = 6;

/**
 * What `?step=` can name. 1 opens the Drawing details dock and 6 is an Agent review
 * link; 3 and 4 parse and mean nothing.
 *
 * DECISION(2026-10-22, abhishek): old `?step=N` links are parsed and ignored, except 1
 * and 6. The other deep-link parameters keep working, so links in Slack and Linear keep
 * opening the drawing.
 */
export type StepParam = 1 | 3 | 4 | typeof AGENT_REVIEW_STEP;

const STEP_PARAM_VALUES: readonly number[] = [1, 3, 4, AGENT_REVIEW_STEP];

/** The deleted Instruments step: a shared link that carries it reads as 3. */
const LEGACY_STEP_ALIASES: Record<number, StepParam> = { 2: 3 };

export function parseStepParam(search: string): StepParam | null {
  const raw = new URLSearchParams(search).get(STEP_PARAM);
  if (raw === null || raw === '') return null;
  const parsed = Number(raw);
  if (STEP_PARAM_VALUES.includes(parsed)) return parsed as StepParam;
  return LEGACY_STEP_ALIASES[parsed] ?? null;
}

/**
 * A drawing link carrying one edit parameter, or the bare drawing when the link
 * names no element.
 */
function buildEditHref(docId: string, param: string, elementId: string | null): string {
  const path = `/pnid/${encodeURIComponent(docId)}`;
  if (!elementId) return path;
  return `${path}?${param}=${encodeURIComponent(elementId)}`;
}

export function buildEquipmentEditHref(
  docId: string,
  equipmentId: string | null,
): string {
  return buildEditHref(docId, EDIT_EQUIPMENT_PARAM, equipmentId);
}

export function parseEditEquipmentId(search: string): string | null {
  return new URLSearchParams(search).get(EDIT_EQUIPMENT_PARAM);
}

export function buildInstrumentEditHref(
  docId: string,
  instrumentId: string | null,
): string {
  return buildEditHref(docId, EDIT_INSTRUMENT_PARAM, instrumentId);
}

export function parseEditInstrumentId(search: string): string | null {
  return new URLSearchParams(search).get(EDIT_INSTRUMENT_PARAM);
}

/**
 * An element link that opens its drawing with Needs review on. The element
 * parameter still names the element the drawing lands on; the step turns on the
 * rail's filter that lists it, so a reviewer who came from the plant queue keeps
 * working down that queue.
 */
export function buildAgentReviewHref(href: string): string {
  return `${href}${href.includes('?') ? '&' : '?'}${STEP_PARAM}=${AGENT_REVIEW_STEP}`;
}

/**
 * The link that opens a drawing scrolled to one mark. A node target centres the
 * canvas on that node; an equipment target selects that equipment. Both are
 * optional, so the same builder serves a plain "open this drawing" link.
 */
export function buildPnidPath(
  docId: string,
  options?: { targetNodeId?: string | null; targetEquipmentId?: string | null },
): string {
  const path = `/pnid/${encodeURIComponent(docId)}`;
  if (!options?.targetNodeId && !options?.targetEquipmentId) {
    return path;
  }
  const params = new URLSearchParams();
  if (options?.targetNodeId) {
    params.set('target_node_id', options.targetNodeId);
  }
  if (options?.targetEquipmentId) {
    params.set('target_equipment_id', options.targetEquipmentId);
  }
  return `${path}?${params.toString()}`;
}

export function parseTargetNodeId(search: string): string | null {
  return new URLSearchParams(search).get('target_node_id');
}

export function parseTargetEquipmentId(search: string): string | null {
  return new URLSearchParams(search).get('target_equipment_id');
}

/**
 * The instrument a link opens the drawing on, without its editor. An equipment
 * or a line target in the same URL outranks it, as does `edit_instrument_id`,
 * so the workspace acts on at most one of them.
 */
export function parseTargetInstrumentId(search: string): string | null {
  return new URLSearchParams(search).get('target_instrument_id');
}
